// components/Seller/ProductCard.tsx
import React, { useState } from "react";
import {
  Edit,
  Trash2,
  Package,
  AlertCircle,
  CheckCircle,
  Clock,
  Layers,
  X,
  PackagePlus,
  ChevronRight,
  Minus,
  Plus,
} from "lucide-react";
import API from "../../API/axios";
import { motion, AnimatePresence } from "framer-motion";

interface ProductImage {
  id: string;
  productId: string;
  image: {
    type: string;
    data: number[];
  };
  createdAt: string;
}

interface Category {
  id: string;
  name: string;
  gstRate: number;
  createdAt: string;
  updatedAt: string;
}

interface Inventory {
  id: string;
  variantId: string;
  stock: number;
  isOutOfStock: boolean;
  updatedAt: string;
}

interface Variant {
  id: string;
  productId: string;
  title: string;
  unit: string;
  value: number;
  price: number;
  sku: string;
  createdAt: string;
  updatedAt: string;
  inventory: Inventory;
}

interface Product {
  id: string;
  sellerId: string;
  categoryId: string;
  name: string;
  description: string;
  status: "PENDING_APPROVAL" | "APPROVED" | "REJECTED";
  createdAt: string;
  updatedAt: string;
  images: ProductImage[];
  category: Category;
  variants: Variant[];
}

interface ProductCardProps {
  product: Product;
  onDelete: (id: string) => void;
  onUpdateStock: (productId: string, variantId: string, stock: number) => void;
  onEdit?: (product: Product) => void;
  viewMode?: "grid" | "list";
  onProductUpdate?: () => void;
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  onDelete,
  onUpdateStock,
  onEdit,
  viewMode = "grid",
  onProductUpdate,
}) => {
  const [expandedVariant, setExpandedVariant] = useState<string | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Edit Modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");

  // Edit form state with variants
  const [editFormData, setEditFormData] = useState({
    name: product.name || "",
    description: product.description || "",
    categoryId: product.categoryId || product.category?.id || "",
  });

  const [variants, setVariants] = useState<any[]>(() => {
    if (product.variants && product.variants?.length > 0) {
      return product.variants.map((v: any) => ({
        id: v.id,
        title: v.title,
        unit: v.unit,
        value: v.value?.toString() || "",
        price: v.price,
        stock: v.inventory?.stock || 0,
      }));
    }
    return [];
  });

  const [images, setImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>(() => {
    if (product.images && product.images?.length > 0) {
      return product.images
        .map((img: any) => {
          if (img.image?.data) {
            try {
              const buffer = img.image.data;
              const base64String = btoa(
                new Uint8Array(buffer)?.reduce(
                  (data, byte) => data + String.fromCharCode(byte),
                  "",
                ),
              );
              return `data:image/jpeg;base64,${base64String}`;
            } catch (error) {
              return "";
            }
          }
          return "";
        })
        .filter((url) => url);
    }
    return [];
  });

  // Inventory Modal state
  const [isInventoryModalOpen, setIsInventoryModalOpen] = useState(false);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [inventoryError, setInventoryError] = useState("");
  const [selectedVariantForInventory, setSelectedVariantForInventory] =
    useState<Variant | null>(null);
  const [inventoryStock, setInventoryStock] = useState<number>(0);

  // Calculate total stock across all variants
  const totalStock =
    product.variants?.reduce(
      (sum, variant) => sum + (variant.inventory?.stock || 0),
      0,
    ) || 0;

  // Get price range if multiple variants
  const getPriceDisplay = () => {
    if (product.variants && product.variants?.length > 0) {
      const prices = product.variants.map((v) => v.price);
      const minPrice = Math.min(...prices);
      const maxPrice = Math.max(...prices);

      if (minPrice === maxPrice) {
        return `₹${minPrice?.toLocaleString()}`;
      }
      return `₹${minPrice?.toLocaleString()} - ₹${maxPrice?.toLocaleString()}`;
    }
    return "Price not set";
  };

  const getStatusBadge = () => {
    if (!product || !product.status) {
      return {
        icon: Clock,
        text: "Unknown",
        className: "bg-gray-100 text-gray-700 border-gray-200",
      };
    }

    switch (product.status) {
      case "APPROVED":
        return {
          icon: CheckCircle,
          text: "Approved",
          className: "bg-green-50 text-green-700 border-green-200",
        };
      case "REJECTED":
        return {
          icon: AlertCircle,
          text: "Rejected",
          className: "bg-red-50 text-red-700 border-red-200",
        };
      case "PENDING_APPROVAL":
        return {
          icon: Clock,
          text: "Pending",
          className: "bg-yellow-50 text-yellow-700 border-yellow-200",
        };
      default:
        return {
          icon: Clock,
          text: product.status || "Unknown",
          className: "bg-gray-100 text-gray-700 border-gray-200",
        };
    }
  };

  // Convert buffer data to base64 image URL
  const getImageUrl = (imageData: ProductImage | undefined): string | null => {
    if (
      !imageData ||
      !imageData.image ||
      !imageData.image.data ||
      imageData.image.data.length === 0
    ) {
      return null;
    }

    try {
      const buffer = imageData.image.data;
      const base64String = btoa(
        new Uint8Array(buffer)?.reduce(
          (data, byte) => data + String.fromCharCode(byte),
          "",
        ),
      );
      return `data:image/jpeg;base64,${base64String}`;
    } catch (error) {
      console.error("Error converting image buffer:", error);
      return null;
    }
  };

  // Safely get first image with fallback
  const getFirstImage = (images: ProductImage[] | undefined) => {
    if (!images || images?.length === 0) return null;
    return getImageUrl(images[0]);
  };

  const firstImageUrl = getFirstImage(product.images);
  const statusBadge = getStatusBadge();
  const StatusIcon = statusBadge.icon;

  // Handle delete with confirmation
  const handleDeleteClick = () => {
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete(product.id);
      setShowDeleteConfirmation(false);
    } catch (error) {
      console.error("Delete error:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
  };

  // Variant management functions
  const addVariant = () => {
    setVariants([
      ...variants,
      {
        title: "",
        unit: "kg",
        value: "",
        price: 0,
        stock: 0,
      },
    ]);
  };

  const removeVariant = (index: number) => {
    setVariants(variants.filter((_, i) => i !== index));
  };

  const updateVariant = (index: number, field: string, value: any) => {
    const updatedVariants = [...variants];
    updatedVariants[index] = { ...updatedVariants[index], [field]: value };
    setVariants(updatedVariants);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setImages([...images, ...files]);

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews((prev) => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index: number) => {
    setImagePreviews(imagePreviews.filter((_, i) => i !== index));
    if (index < images.length) {
      setImages(images.filter((_, i) => i !== index));
    }
  };

  const openEditModal = () => {
    setEditFormData({
      name: product.name,
      description: product.description,
      categoryId: product.categoryId || product.category?.id || "",
    });
    setVariants(
      product.variants.map((v: any) => ({
        id: v.id,
        title: v.title,
        unit: v.unit,
        value: v.value?.toString() || "",
        price: v.price,
        stock: v.inventory?.stock || 0,
      })),
    );
    setImages([]);
    setImagePreviews(firstImageUrl ? [firstImageUrl] : []);
    setEditError("");
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEditLoading(true);
    setEditError("");

    try {
      const formData = new FormData();
      formData.append("name", editFormData.name.trim());
      formData.append("description", editFormData.description.trim());
      formData.append("categoryId", editFormData.categoryId.trim());

      const variantsData = variants.map((variant) => {
        const variantData: any = {
          title: variant.title.trim(),
          unit: variant.unit,
          value: variant.value.trim(),
          price: Number(variant.price),
          stock: Number(variant.stock),
        };
        if (variant.id) {
          variantData.id = variant.id;
        }
        return variantData;
      });

      formData.append("variants", JSON.stringify(variantsData));
      images.forEach((image) => {
        formData.append("images", image);
      });

      const response = await API.put(
        `/seller/update-product/${product.id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      alert("Product updated successfully!");
      setIsEditModalOpen(false);
      if (onProductUpdate) {
        onProductUpdate();
      }
      if (onEdit) {
        onEdit(response.data);
      }
    } catch (err: any) {
      console.error("Update error:", err.response?.data || err);
      setEditError(err.response?.data?.message || "Failed to update product");
    } finally {
      setEditLoading(false);
    }
  };

  const openInventoryModal = (variant: Variant) => {
    setSelectedVariantForInventory(variant);
    setInventoryStock(variant.inventory?.stock || 0);
    setInventoryError("");
    setIsInventoryModalOpen(true);
  };

  const handleInventorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedVariantForInventory) return;

    setInventoryLoading(true);
    setInventoryError("");

    try {
      await onUpdateStock(
        product.id,
        selectedVariantForInventory.id,
        inventoryStock,
      );
      alert("Inventory updated successfully!");
      setIsInventoryModalOpen(false);
      if (onProductUpdate) {
        onProductUpdate();
      }
    } catch (err: any) {
      setInventoryError(
        err.response?.data?.message || "Failed to update inventory",
      );
    } finally {
      setInventoryLoading(false);
    }
  };

  // Delete Confirmation Modal Component
  const DeleteConfirmationModal = () => (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden"
      >
        <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4">
          <h2 className="text-xl font-bold text-white">Delete Product</h2>
        </div>

        <div className="p-6">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <Trash2 size={32} className="text-red-600" />
            </div>
          </div>

          <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
            Are you sure?
          </h3>
          <p className="text-gray-600 text-center mb-6">
            You are about to delete "{product.name}". This action cannot be
            undone.
          </p>

          <div className="flex gap-3">
            <button
              onClick={handleCancelDelete}
              disabled={isDeleting}
              className="flex-1 px-4 py-2.5 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="flex-1 px-4 py-2.5 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDeleting ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Deleting...
                </div>
              ) : (
                "Delete Product"
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );

  // Edit Modal Component
  const EditModal = () => (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
      >
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">Edit Product</h2>
          <button
            onClick={() => setIsEditModalOpen(false)}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleEditSubmit} className="p-6 space-y-6">
          {editError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-2xl text-sm">
              {editError}
            </div>
          )}

          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Basic Information
            </h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Product Name *
              </label>
              <input
                type="text"
                value={editFormData.name}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, name: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description *
              </label>
              <textarea
                value={editFormData.description}
                onChange={(e) =>
                  setEditFormData({
                    ...editFormData,
                    description: e.target.value,
                  })
                }
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                required
              />
            </div>
          </div>

          {/* Variants Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Product Variants
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  {variants.some((v) => v.id)
                    ? "Existing variants will be updated"
                    : "All variants are new"}
                </p>
              </div>
              <button
                type="button"
                onClick={addVariant}
                className="inline-flex items-center px-3 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600"
              >
                <Plus size={16} className="mr-1" />
                Add Variant
              </button>
            </div>

            {variants?.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <p className="text-gray-500">No variants added yet</p>
                <button
                  type="button"
                  onClick={addVariant}
                  className="mt-2 text-green-950 hover:text-green-700 font-medium"
                >
                  Add your first variant
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {variants.map((variant, index) => (
                  <div
                    key={index}
                    className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-medium text-gray-900">
                          Variant {index + 1}
                        </h4>
                        {variant.id && (
                          <p className="text-xs text-blue-600 mt-0.5">
                            ID: {variant.id} (will be updated)
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeVariant(index)}
                        className="text-red-500 hover:text-red-600"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <input
                        type="text"
                        placeholder="Title (e.g., 1 KG)"
                        value={variant.title}
                        onChange={(e) =>
                          updateVariant(index, "title", e.target.value)
                        }
                        className="px-3 py-2 border border-gray-300 rounded-lg"
                        required
                      />
                      <select
                        value={variant.unit}
                        onChange={(e) =>
                          updateVariant(index, "unit", e.target.value)
                        }
                        className="px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="kg">kg</option>
                        <option value="g">g</option>
                        <option value="l">l</option>
                        <option value="ml">ml</option>
                        <option value="pcs">pcs</option>
                      </select>
                      <input
                        type="text"
                        placeholder="Value (e.g., 1)"
                        value={variant.value}
                        onChange={(e) =>
                          updateVariant(index, "value", e.target.value)
                        }
                        className="px-3 py-2 border border-gray-300 rounded-lg"
                        required
                      />
                      <input
                        type="number"
                        placeholder="Price (₹)"
                        value={variant.price}
                        onChange={(e) =>
                          updateVariant(
                            index,
                            "price",
                            parseFloat(e.target.value) || 0,
                          )
                        }
                        className="px-3 py-2 border border-gray-300 rounded-lg"
                        required
                      />
                      <input
                        type="number"
                        placeholder="Stock"
                        value={variant.stock}
                        onChange={(e) =>
                          updateVariant(
                            index,
                            "stock",
                            parseInt(e.target.value) || 0,
                          )
                        }
                        className="px-3 py-2 border border-gray-300 rounded-lg"
                        required
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Images Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Product Images
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {imagePreviews.map((preview, index) => (
                <div key={index} className="relative group">
                  <img
                    src={preview}
                    alt={`Product ${index + 1}`}
                    className="w-full h-32 object-cover rounded-lg border border-gray-200"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
              <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-emerald-500 bg-gray-50">
                <PackagePlus size={24} className="text-gray-400" />
                <span className="text-xs text-gray-500 mt-1">Upload Image</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>
            </div>
            <p className="text-xs text-gray-500">
              Note: Existing images will be kept. New images will be added.
            </p>
          </div>

          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setIsEditModalOpen(false)}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={editLoading}
              className="flex-1 px-4 py-2 bg-green-950 text-white rounded-lg hover:shadow-lg disabled:opacity-50"
            >
              {editLoading ? "Updating..." : "Update Product"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );

  // Inventory Modal Component
  const InventoryModal = () => (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white rounded-3xl max-w-md w-full shadow-2xl"
      >
        <div className="border-b border-gray-100 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">Update Inventory</h2>
          <button
            onClick={() => setIsInventoryModalOpen(false)}
            className="p-2 hover:bg-gray-100 rounded-xl"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {selectedVariantForInventory && (
          <form onSubmit={handleInventorySubmit} className="p-6 space-y-5">
            {inventoryError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-2xl text-sm">
                {inventoryError}
              </div>
            )}

            <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100">
              <p className="text-sm font-semibold text-gray-900 mb-3">
                Variant Details
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Title:</span>
                  <span className="font-semibold">
                    {selectedVariantForInventory.title}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Unit:</span>
                  <span>
                    {selectedVariantForInventory.value}{" "}
                    {selectedVariantForInventory.unit}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Price:</span>
                  <span className="font-semibold text-green-600">
                    ₹{selectedVariantForInventory.price}
                  </span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                  <span className="text-gray-500">Current Stock:</span>
                  <span
                    className={`font-bold ${selectedVariantForInventory.inventory?.stock > 0 ? "text-green-600" : "text-red-600"}`}
                  >
                    {selectedVariantForInventory.inventory?.stock || 0} units
                  </span>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Stock Quantity
              </label>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() =>
                    setInventoryStock(Math.max(0, inventoryStock - 1))
                  }
                  className="w-10 h-10 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
                >
                  <Minus size={18} />
                </button>
                <input
                  type="number"
                  min="0"
                  value={inventoryStock}
                  onChange={(e) =>
                    setInventoryStock(parseInt(e.target.value) || 0)
                  }
                  className="flex-1 h-12 px-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 text-center"
                />
                <button
                  type="button"
                  onClick={() => setInventoryStock(inventoryStock + 1)}
                  className="w-10 h-10 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
                >
                  <Plus size={18} />
                </button>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={inventoryLoading}
                className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 text-white py-3 rounded-xl font-bold disabled:opacity-50"
              >
                {inventoryLoading ? "Updating..." : "Update Stock"}
              </button>
              <button
                type="button"
                onClick={() => setIsInventoryModalOpen(false)}
                className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-bold hover:bg-gray-200"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );

  // Grid View
  if (viewMode === "grid") {
    return (
      <>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          whileHover={{ y: -4 }}
          className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
        >
          <div className="relative h-48 bg-gradient-to-br from-gray-100 to-gray-50 overflow-hidden">
            {firstImageUrl ? (
              <img
                src={firstImageUrl}
                alt={product.name}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Package size={48} className="text-gray-300" />
              </div>
            )}
            <div className="absolute top-3 right-3">
              <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold backdrop-blur-sm border ${statusBadge.className} bg-white/90`}
              >
                <StatusIcon size={12} />
                {statusBadge.text}
              </span>
            </div>
            {product.variants && product.variants?.length > 0 && (
              <div className="absolute top-3 left-3">
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold bg-black/60 backdrop-blur-sm text-white">
                  <Layers size={12} />
                  {product.variants?.length} Variants
                </span>
              </div>
            )}
          </div>

          <div className="p-5">
            <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-1">
              {product.name}
            </h3>
            <p className="text-sm text-gray-500 mb-3 line-clamp-2">
              {product.description || "No description available"}
            </p>
            <div className="mb-4">
              <span className="text-2xl font-bold text-green-950">
                {getPriceDisplay()}
              </span>
            </div>

            <div className="mb-4 p-3 bg-gray-50 rounded-xl">
              <div className="flex justify-between mb-2">
                <span className="text-xs font-bold text-gray-500">
                  Total Stock
                </span>
                <span
                  className={`text-sm font-bold ${totalStock > 0 ? "text-green-600" : "text-red-600"}`}
                >
                  {totalStock} units
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div
                  className={`h-full rounded-full transition-all ${totalStock > 0 ? "bg-green-500" : "bg-red-500"}`}
                  style={{
                    width: `${Math.min((totalStock / 100) * 100, 100)}%`,
                  }}
                />
              </div>
            </div>

            {product.variants && product.variants?.length > 0 && (
              <div className="mb-4">
                <button
                  onClick={() =>
                    setExpandedVariant(
                      expandedVariant === product.id ? null : product.id,
                    )
                  }
                  className="w-full flex items-center justify-between p-2 bg-gray-50 rounded-xl hover:bg-gray-100"
                >
                  <span className="text-xs font-bold text-gray-600">
                    Variants ({product.variants?.length})
                  </span>
                  <ChevronRight
                    size={16}
                    className={`transform transition-transform ${expandedVariant === product.id ? "rotate-90" : ""}`}
                  />
                </button>

                <AnimatePresence>
                  {expandedVariant === product.id && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-2 space-y-2"
                    >
                      {product.variants.map((variant) => (
                        <div
                          key={variant.id}
                          className="p-2 bg-gray-50 rounded-xl"
                        >
                          <div className="flex justify-between items-start mb-1">
                            <div>
                              <p className="text-sm font-semibold">
                                {variant.title}
                              </p>
                              <p className="text-xs text-gray-500">
                                {variant.value} {variant.unit}
                              </p>
                            </div>
                            <button
                              onClick={() => openInventoryModal(variant)}
                              className="p-1.5 text-green-600 hover:bg-green-100 rounded-lg"
                              title="Update stock"
                            >
                              <PackagePlus size={14} />
                            </button>
                          </div>
                          <div className="flex justify-between items-center mt-1">
                            <span className="text-sm font-bold text-green-600">
                              ₹{variant.price}
                            </span>
                            <span
                              className={`text-xs ${variant.inventory?.stock > 0 ? "text-green-600" : "text-red-600"}`}
                            >
                              Stock: {variant.inventory?.stock || 0}
                            </span>
                          </div>
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            <div className="flex gap-2 pt-2 border-t border-gray-100">
              <button
                onClick={() => (onEdit ? onEdit(product) : openEditModal())}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 text-blue-600 text-sm hover:bg-blue-300 font-bold rounded-xl  transition-all"
              >
                <Edit size={16} />
                Edit
              </button>
              <button
                onClick={handleDeleteClick}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 text-red-700 text-sm font-bold rounded-xl hover:bg-red-100 transition-all"
              >
                <Trash2 size={16} />
                Delete
              </button>
            </div>
          </div>
        </motion.div>

        {isEditModalOpen && <EditModal />}
        {isInventoryModalOpen && <InventoryModal />}
        {showDeleteConfirmation && <DeleteConfirmationModal />}
      </>
    );
  }

  // List View
  return (
    <>
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        whileHover={{ x: 4 }}
        className="group bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden"
      >
        <div className="flex flex-col md:flex-row">
          <div className="md:w-32 h-32 bg-gradient-to-br from-gray-100 to-gray-50 relative overflow-hidden">
            {firstImageUrl ? (
              <img
                src={firstImageUrl}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Package size={32} className="text-gray-300" />
              </div>
            )}
          </div>

          <div className="flex-1 p-4">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-bold text-gray-900">
                    {product.name}
                  </h3>
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${statusBadge.className}`}
                  >
                    <StatusIcon size={10} />
                    {statusBadge.text}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mb-3 line-clamp-2">
                  {product.description}
                </p>
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <span className="text-xl font-bold text-green-600">
                    {getPriceDisplay()}
                  </span>
                  <span
                    className={`text-xs font-bold ${totalStock > 0 ? "text-green-600" : "text-red-600"}`}
                  >
                    Stock: {totalStock} units
                  </span>
                  {product.variants && product.variants?.length > 0 && (
                    <span className="flex items-center gap-1 text-xs text-gray-500">
                      <Layers size={12} />
                      {product.variants?.length} variants
                    </span>
                  )}
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => (onEdit ? onEdit(product) : openEditModal())}
                  className="px-4 py-2  text-blue-800 text-sm font-bold rounded-xl hover:bg-blue-100 flex items-center gap-2"
                >
                  <Edit size={14} />
                  Edit
                </button>
                <button
                  onClick={handleDeleteClick}
                  className="px-4 py-2  text-red-800 text-sm font-bold rounded-xl hover:bg-red-100 flex items-center gap-2"
                >
                  <Trash2 size={14} />
                  Delete
                </button>
              </div>
            </div>

            {product.variants && product.variants?.length > 0 && (
              <div className="mt-4 pt-3 border-t border-gray-100">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {product.variants.map((variant) => (
                    <div key={variant.id} className="p-2 bg-gray-50 rounded-xl">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-sm font-semibold">
                            {variant.title}
                          </p>
                          <p className="text-xs text-gray-500">
                            {variant.value} {variant.unit}
                          </p>
                        </div>
                        <button
                          onClick={() => openInventoryModal(variant)}
                          className="p-1.5 text-green-600 hover:bg-green-100 rounded-lg"
                          title="Update stock"
                        >
                          <PackagePlus size={14} />
                        </button>
                      </div>
                      <div className="flex justify-between items-center mt-2 pt-1 border-t border-gray-200">
                        <span className="text-sm font-bold text-green-600">
                          ₹{variant.price}
                        </span>
                        <span
                          className={`text-xs ${variant.inventory?.stock > 0 ? "text-green-600" : "text-red-600"}`}
                        >
                          Stock: {variant.inventory?.stock || 0}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {isEditModalOpen && <EditModal />}
      {isInventoryModalOpen && <InventoryModal />}
      {showDeleteConfirmation && <DeleteConfirmationModal />}
    </>
  );
};

export default ProductCard;
